import { getDigisakaAuthHeaders } from '../../../config/digisakaApi';
import { getFarmWebgisApiBaseUrl } from '../../../config/farmWebgisApi';
import { digisakaAuthRequestInit, digisakaMeteredAuthHeaders } from '../../../config/digisakaGptInference';

export type AdminPcodes = {
  adm1_pcode?: string | null;
  adm2_pcode?: string | null;
  adm3_pcode?: string | null;
  adm4_pcode?: string | null;
};

export type CentroidLocation = {
  region: string;
  province: string;
  municipality?: string;
  barangay?: string;
  adminPcodes?: AdminPcodes;
};

export type DigisakaLocationCodes = {
  region: string;
  province: string;
  municipality: string;
  barangay: string;
  /** digisaka.app ``philippine_barangays.id`` — required for LGU farm create. */
  barangayId?: string;
};

export type RegionRow = { region_code: string; region_description: string };
export type ProvinceRow = { province_name: string; province_code: string };

/** FastAPI returns flat JSON; proxies may wrap under ``data``. */
export function parseCentroidPayload(raw: unknown): CentroidLocation | null {
  if (!raw || typeof raw !== 'object') return null;
  const root = raw as Record<string, unknown>;
  const o =
    root.region != null && root.province != null
      ? root
      : ((root.data ?? root.result ?? root.payload) as Record<string, unknown> | undefined);
  if (!o || typeof o !== 'object') return null;
  const region = o.region;
  const province = o.province;
  if (region == null || province == null) return null;

  const adminRaw = (o.admin_pcodes ?? root.admin_pcodes) as AdminPcodes | Record<string, unknown> | undefined;
  const geeProps = (o.gee_properties ?? root.gee_properties) as Record<string, unknown> | undefined;

  // Prefer explicit admin_pcodes; fall back to GEE feature properties (ADM*_PCODE).
  const adminPcodes: AdminPcodes = {
    adm1_pcode:
      (adminRaw as AdminPcodes | undefined)?.adm1_pcode ??
      (adminRaw as Record<string, unknown> | undefined)?.ADM1_PCODE ??
      geeProps?.ADM1_PCODE ??
      null,
    adm2_pcode:
      (adminRaw as AdminPcodes | undefined)?.adm2_pcode ??
      (adminRaw as Record<string, unknown> | undefined)?.ADM2_PCODE ??
      geeProps?.ADM2_PCODE ??
      null,
    adm3_pcode:
      (adminRaw as AdminPcodes | undefined)?.adm3_pcode ??
      (adminRaw as Record<string, unknown> | undefined)?.ADM3_PCODE ??
      geeProps?.ADM3_PCODE ??
      null,
    adm4_pcode:
      (adminRaw as AdminPcodes | undefined)?.adm4_pcode ??
      (adminRaw as Record<string, unknown> | undefined)?.ADM4_PCODE ??
      geeProps?.ADM4_PCODE ??
      null,
  };

  return {
    region: String(region).trim(),
    province: String(province).trim(),
    municipality: o.municipality != null ? String(o.municipality).trim() : undefined,
    barangay: o.barangay != null ? String(o.barangay).trim() : undefined,
    adminPcodes,
  };
}

function stripPhPcode(pcode: string | null | undefined): string {
  if (!pcode) return '';
  return String(pcode).trim().toUpperCase().replace(/^PH/i, '').replace(/\D/g, '');
}

/**
 * GEE GADM-style P-codes embed an extra leading 0 after the region for PH
 * (e.g. PH04034 → digits 04034). Digisaka / PSGC use 0434 / 043406 / …
 * Convert: region + rest-without-leading-0 → 0434, 043406, 043406002.
 */
function geeDigitsToDigisaka(digits: string, region: string): string {
  const d = String(digits || '').replace(/\D/g, '');
  const r = String(region || '').replace(/\D/g, '');
  if (!d) return '';
  if (r && d.startsWith(r) && d.length > r.length) {
    let rest = d.slice(r.length);
    if (rest.startsWith('0')) rest = rest.slice(1);
    return r + rest;
  }
  // Already Digisaka-shaped (0434) or non-PH — keep as-is.
  return d;
}

/** Map GEE ADM P-codes to Digisaka mobile/outbound code strings. */
export function adminPcodesToDigisakaCodes(pcodes?: AdminPcodes | null): DigisakaLocationCodes {
  const adm1 = stripPhPcode(pcodes?.adm1_pcode);
  const adm2 = stripPhPcode(pcodes?.adm2_pcode);
  const adm3 = stripPhPcode(pcodes?.adm3_pcode);
  const adm4 = stripPhPcode(pcodes?.adm4_pcode);
  const region = adm1.length >= 2 ? adm1.slice(0, 2) : adm1;

  return {
    region,
    province: geeDigitsToDigisaka(adm2, region),
    municipality: geeDigitsToDigisaka(adm3, region),
    // digisaka.app farm create expects location *codes* — use GEE ADM→Digisaka P-codes.
    barangay: geeDigitsToDigisaka(adm4, region),
  };
}

export async function fetchCentroidLocation(
  longitude: number,
  latitude: number
): Promise<CentroidLocation | null> {
  const base = getFarmWebgisApiBaseUrl();
  const url = `${base}/api/philippines/region-province-from-centroid/?longitude=${encodeURIComponent(longitude)}&latitude=${encodeURIComponent(latitude)}`;
  const response = await fetch(url, digisakaAuthRequestInit({ headers: digisakaMeteredAuthHeaders() }));
  if (!response.ok) return null;
  const body = await response.json();
  return parseCentroidPayload(body);
}

export type AdminPcodePointResult = {
  id: string;
  longitude: number;
  latitude: number;
  found: boolean;
  province?: string | null;
  municipality?: string | null;
  barangay?: string | null;
  admin_pcodes?: {
    adm2_pcode?: string | null;
    adm3_pcode?: string | null;
    adm4_pcode?: string | null;
  };
};

export type BlockAdminSummary = {
  blockNumber: number;
  id: string;
  locationKey: string;
  province: string;
  municipality: string;
  barangay: string;
  isMajority: boolean;
  isOutlier: boolean;
};

export type BlockAdminGroup = {
  locationKey: string;
  blockNumbers: number[];
  pointIds: string[];
  province: string;
  municipality: string;
  barangay: string;
  /** Which admin levels differ from the majority group. */
  differsIn: Array<'province' | 'municipality' | 'barangay'>;
};

function adminLocationKey(row: AdminPcodePointResult): string | null {
  const mun = String(row.admin_pcodes?.adm3_pcode || '').replace(/\D/g, '');
  const brgy = String(row.admin_pcodes?.adm4_pcode || '').replace(/\D/g, '');
  if (brgy) return `b:${brgy}`;
  if (mun) return `m:${mun}`;
  return null;
}

function blockNumberFromId(id: string): number {
  const n = Number(String(id).replace(/\D/g, '')) || Number(id);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** One GEE filterBounds batch for many block centroids (fast multi-block check). */
export async function fetchAdminPcodesForPoints(
  points: Array<{ id: string; longitude: number; latitude: number }>
): Promise<AdminPcodePointResult[]> {
  if (!points.length) return [];
  const base = getFarmWebgisApiBaseUrl();
  const response = await fetch(`${base}/api/philippines/admin-pcodes-from-points/`, {
    ...digisakaAuthRequestInit({
      method: 'POST',
      headers: {
        ...digisakaMeteredAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ points }),
    }),
  });
  if (!response.ok) return [];
  const body = await response.json();
  return Array.isArray(body?.results) ? body.results : [];
}

/**
 * Compare all block centroids (barangay ADM4 when present, else municipality ADM3).
 * Different admin area → those block numbers cannot share one field.
 */
export function analyzeBlockAdminLocations(results: AdminPcodePointResult[]): {
  outlierBlockNumbers: number[];
  /** Point id (usually "1","2",…) from the majority location group — use for name autofill. */
  majorityPointId: string | null;
  checkedCount: number;
  foundCount: number;
  blocks: BlockAdminSummary[];
  groups: BlockAdminGroup[];
  /** Human-readable: e.g. "municipality and barangay". */
  differLevelsLabel: string;
} {
  const found = results.filter((r) => adminLocationKey(r));
  if (found.length === 0) {
    return {
      outlierBlockNumbers: [],
      majorityPointId: results[0]?.id ?? null,
      checkedCount: results.length,
      foundCount: 0,
      blocks: [],
      groups: [],
      differLevelsLabel: '',
    };
  }

  const counts = new Map<string, number>();
  for (const row of found) {
    const key = adminLocationKey(row)!;
    counts.set(key, (counts.get(key) || 0) + 1);
  }

  let majorityKey = '';
  let best = 0;
  for (const [code, n] of counts) {
    if (n > best) {
      best = n;
      majorityKey = code;
    }
  }

  const majorityRow = found.find((r) => adminLocationKey(r) === majorityKey) ?? found[0];
  const majorityPointId = majorityRow?.id ?? null;
  const majorityProv = String(majorityRow?.province || '').trim();
  const majorityMun = String(majorityRow?.municipality || '').trim();
  const majorityBrgy = String(majorityRow?.barangay || '').trim();

  const blocks: BlockAdminSummary[] = found
    .map((row) => {
      const key = adminLocationKey(row)!;
      const isMajority = key === majorityKey;
      return {
        blockNumber: blockNumberFromId(row.id),
        id: row.id,
        locationKey: key,
        province: String(row.province || '').trim() || '—',
        municipality: String(row.municipality || '').trim() || '—',
        barangay: String(row.barangay || '').trim() || '—',
        isMajority,
        isOutlier: !isMajority,
      };
    })
    .filter((b) => b.blockNumber > 0)
    .sort((a, b) => a.blockNumber - b.blockNumber);

  const groupMap = new Map<string, BlockAdminGroup>();
  for (const row of found) {
    const key = adminLocationKey(row)!;
    const bn = blockNumberFromId(row.id);
    if (bn <= 0) continue;
    let g = groupMap.get(key);
    if (!g) {
      const province = String(row.province || '').trim() || '—';
      const municipality = String(row.municipality || '').trim() || '—';
      const barangay = String(row.barangay || '').trim() || '—';
      const differsIn: Array<'province' | 'municipality' | 'barangay'> = [];
      if (key !== majorityKey) {
        if (province !== majorityProv && province !== '—' && majorityProv) differsIn.push('province');
        if (municipality !== majorityMun && municipality !== '—' && majorityMun) {
          differsIn.push('municipality');
        } else if (
          String(row.admin_pcodes?.adm3_pcode || '').replace(/\D/g, '') !==
          String(majorityRow?.admin_pcodes?.adm3_pcode || '').replace(/\D/g, '')
        ) {
          differsIn.push('municipality');
        }
        if (barangay !== majorityBrgy && barangay !== '—' && majorityBrgy) {
          differsIn.push('barangay');
        } else if (
          String(row.admin_pcodes?.adm4_pcode || '').replace(/\D/g, '') !==
          String(majorityRow?.admin_pcodes?.adm4_pcode || '').replace(/\D/g, '')
        ) {
          differsIn.push('barangay');
        }
        if (differsIn.length === 0) differsIn.push('barangay');
      }
      g = {
        locationKey: key,
        blockNumbers: [],
        pointIds: [],
        province,
        municipality,
        barangay,
        differsIn,
      };
      groupMap.set(key, g);
    }
    if (!g.blockNumbers.includes(bn)) g.blockNumbers.push(bn);
    if (!g.pointIds.includes(row.id)) g.pointIds.push(row.id);
  }

  const groups = [...groupMap.values()]
    .map((g) => ({
      ...g,
      blockNumbers: [...g.blockNumbers].sort((a, b) => a - b),
    }))
    .sort((a, b) => (a.blockNumbers[0] || 0) - (b.blockNumbers[0] || 0));

  const differLevels = new Set<'province' | 'municipality' | 'barangay'>();
  for (const g of groups) {
    for (const d of g.differsIn) differLevels.add(d);
  }
  const differLevelsLabel = [...differLevels].join(' and ');

  const outliers = blocks.filter((b) => b.isOutlier).map((b) => b.blockNumber);

  return {
    outlierBlockNumbers: [...new Set(outliers)].sort((a, b) => a - b),
    majorityPointId,
    checkedCount: results.length,
    foundCount: found.length,
    blocks,
    groups,
    differLevelsLabel,
  };
}

/** @deprecated Prefer analyzeBlockAdminLocations */
export function findAdminOutlierBlockNumbers(
  results: AdminPcodePointResult[]
): number[] {
  return analyzeBlockAdminLocations(results).outlierBlockNumbers;
}

export type BarangayRow = {
  id?: number | string;
  barangay_name?: string;
  barangay_code?: string;
  brgy_code?: string;
  code?: string;
  psgc_code?: string;
};

/** Build single-option dropdown rows from WebGIS/GEE centroid (no digisaka.app list calls). */
export function centroidToDropdownRows(loc: CentroidLocation): {
  regions: RegionRow[];
  provinces: ProvinceRow[];
  cities: Array<{ city_code: string; city_name: string }>;
  barangays: BarangayRow[];
  codes: DigisakaLocationCodes;
} {
  const codes = adminPcodesToDigisakaCodes(loc.adminPcodes);
  return {
    codes,
    regions: loc.region
      ? [{ region_code: codes.region, region_description: loc.region }]
      : [],
    provinces: loc.province
      ? [{ province_name: loc.province, province_code: codes.province }]
      : [],
    cities:
      loc.municipality
        ? [{ city_name: loc.municipality, city_code: codes.municipality }]
        : [],
    barangays:
      loc.barangay || codes.barangay
        ? [
            {
              barangay_name: loc.barangay || codes.barangay,
              barangay_code: codes.barangay,
              code: codes.barangay,
              psgc_code: codes.barangay,
              // Prefer P-code for save payload; digisaka.app expects codes, not list-row ids.
              id: codes.barangay || undefined,
            },
          ]
        : [],
  };
}

export function normalizeRegionsFromApiPayload(body: unknown): RegionRow[] {
  const raw = Array.isArray(body)
    ? body
    : (body as { data?: unknown; regions?: unknown; items?: unknown })?.data ??
      (body as { regions?: unknown })?.regions ??
      (body as { items?: unknown })?.items ??
      [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r: Record<string, unknown>) => ({
      region_code: String(r?.region_code ?? r?.code ?? r?.psgc_code ?? '').trim(),
      region_description: String(
        r?.region_description ?? r?.region_name ?? r?.name ?? r?.label ?? ''
      ).trim(),
    }))
    .filter((r) => r.region_description.length > 0)
    .sort((a, b) =>
      a.region_description.localeCompare(b.region_description, undefined, { sensitivity: 'base' })
    );
}

export function normalizeProvincesFromApiPayload(data: unknown[]): ProvinceRow[] {
  if (!Array.isArray(data)) return [];
  return data
    .map((p: unknown) => {
      if (typeof p === 'string') return { province_name: p.trim(), province_code: '' };
      const row = p as Record<string, unknown>;
      return {
        province_name: String(row?.province_name ?? row?.province_description ?? row?.name ?? '').trim(),
        province_code: String(row?.province_code ?? row?.code ?? '').trim(),
      };
    })
    .filter((p) => p.province_name.length > 0);
}

const DIGISAKA_MOBILE_API = 'https://digisaka.app/api/mobile';

function mobileHeaders(): HeadersInit {
  return getDigisakaAuthHeaders() as HeadersInit;
}

export async function fetchRegionsListFromApi(): Promise<RegionRow[]> {
  const response = await fetch(`${DIGISAKA_MOBILE_API}/fetch-regions`, { headers: mobileHeaders() });
  if (!response.ok) throw new Error('Failed to fetch regions');
  const data = await response.json();
  return normalizeRegionsFromApiPayload(data);
}

export async function fetchProvincesListForRegionCode(regionCode: string): Promise<ProvinceRow[]> {
  const code = String(regionCode || '').trim();
  if (!code) return [];
  const response = await fetch(
    `${DIGISAKA_MOBILE_API}/fetch-provinces?region_code=${encodeURIComponent(code)}`,
    { headers: mobileHeaders() }
  );
  if (!response.ok) return [];
  const data = await response.json();
  if (!data.success || !Array.isArray(data.data)) return [];
  // Digisaka often returns the full province catalog; keep only this region.
  const forRegion = data.data.filter(
    (p: Record<string, unknown>) => String(p?.region_code ?? '').trim() === code
  );
  return normalizeProvincesFromApiPayload(forRegion.length > 0 ? forRegion : []).sort((a, b) =>
    a.province_name.localeCompare(b.province_name, undefined, { sensitivity: 'base' })
  );
}

export async function fetchCitiesListForProvinceCode(
  provinceCode: string
): Promise<Array<{ city_code: string; city_name: string; province_code?: string }>> {
  const code = String(provinceCode || '').trim();
  if (!code) return [];
  const response = await fetch(
    `${DIGISAKA_MOBILE_API}/fetch-cities?province_code=${encodeURIComponent(code)}`,
    { headers: mobileHeaders() }
  );
  if (!response.ok) return [];
  const data = await response.json();
  if (!data.success || !Array.isArray(data.data)) return [];
  // Digisaka often returns the full cities catalog; keep only this province.
  return [...data.data]
    .filter((c: { province_code?: string; city_code?: string }) => {
      const pc = String(c?.province_code ?? '').trim();
      if (pc) return pc === code;
      // Fallback: city_code usually starts with province_code (e.g. 112403 → 1124).
      return String(c?.city_code ?? '').startsWith(code);
    })
    .sort((a: { city_name: string }, b: { city_name: string }) =>
      a.city_name.localeCompare(b.city_name, undefined, { sensitivity: 'base' })
    );
}

export async function fetchBarangaysListForCityCode(
  cityCode: string
): Promise<Array<{ id?: number | string; barangay_name?: string; barangay_code?: string; brgy_code?: string; code?: string; psgc_code?: string }>> {
  const response = await fetch(
    `${DIGISAKA_MOBILE_API}/fetch-barangays?city_code=${encodeURIComponent(cityCode)}`,
    { headers: mobileHeaders() }
  );
  if (!response.ok) return [];
  const data = await response.json();
  if (!data.success || !data.data || !Array.isArray(data.data)) return [];
  return [...data.data].sort(
    (a: { barangay_name?: string }, b: { barangay_name?: string }) =>
      (a.barangay_name || '').localeCompare(b.barangay_name || '', undefined, { sensitivity: 'base' })
  );
}

/** Digisaka create/update expect numeric PSGC-style codes (same as GEE ADM P-codes). */
function asAdminCode(value: unknown): string {
  if (value == null) return '';
  const digits = String(value).trim().replace(/^PH/i, '').replace(/\D/g, '');
  return digits;
}

/**
 * Build Digisaka location codes for farm create/update.
 *
 * Prefer WebGIS/GEE→Digisaka P-codes from centroid autofill (`resolvedCodes`).
 * Dropdown row codes are used only after the user manually edits location
 * (digisaka.app lists). Never send place names.
 */
export function resolveLocationCodesForPayload(
  resolvedCodes: DigisakaLocationCodes | null,
  selectedRegion: RegionRow | undefined,
  selectedProvince: ProvinceRow | undefined,
  selectedCity: { city_code?: string } | undefined,
  selectedBarangay:
    | {
        barangay_code?: string;
        brgy_code?: string;
        code?: string;
        psgc_code?: string;
        id?: string | number;
      }
    | undefined,
  formValues: { region: string; province: string; municipality: string; barangay: string }
): DigisakaLocationCodes {
  const rowId = (() => {
    const candidates = [resolvedCodes?.barangayId, selectedBarangay?.id];
    for (const value of candidates) {
      const raw = String(value ?? '').trim();
      if (/^\d{1,7}$/.test(raw)) return raw;
    }
    return '';
  })();

  // Prefer PSGC-style codes from the row; avoid short DB ids when P-code exists.
  const barangayFromRow = asAdminCode(
    selectedBarangay?.barangay_code ??
      selectedBarangay?.brgy_code ??
      selectedBarangay?.code ??
      selectedBarangay?.psgc_code ??
      selectedBarangay?.id
  );

  // Autofill path: resolved GEE codes win so we never block on digisaka.app lists.
  const region =
    asAdminCode(resolvedCodes?.region) ||
    asAdminCode(selectedRegion?.region_code) ||
    asAdminCode(formValues.region);
  const province =
    asAdminCode(resolvedCodes?.province) ||
    asAdminCode(selectedProvince?.province_code) ||
    asAdminCode(formValues.province);
  const municipality =
    asAdminCode(resolvedCodes?.municipality) ||
    asAdminCode(selectedCity?.city_code) ||
    asAdminCode(formValues.municipality);
  const barangay =
    rowId ||
    asAdminCode(resolvedCodes?.barangay) ||
    barangayFromRow ||
    asAdminCode(formValues.barangay);

  return { region, province, municipality, barangay, barangayId: rowId || undefined };
}

/** Case-insensitive barangay / place-name match for Digisaka list rows. */
export function matchBarangayByName<T extends { barangay_name?: string }>(
  rows: T[],
  name: string | null | undefined
): T | undefined {
  const target = String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!target) return undefined;
  return rows.find((b) => {
    const n = String(b.barangay_name || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    return n === target;
  });
}
